





























































































































































import Vue from 'vue'
import { Product, ApiResponse, Rate } from '@/interfaces'
import { mapState } from 'vuex'
import { ElForm } from 'element-ui/types/form'

export default Vue.extend({
  props: {
    showFormModal: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object as () => Product,
      required: false,
      default: () => {
        return {
          structureId: null,
          active: true as boolean | null,
        }
      },
    },
    structure: {
      type: Number,
      required: false,
      default: null,
    },
    fetchProductId: {
      type: String,
      required: false,
      default: null,
    },
    isDisabled: Boolean,
  },
  data() {
    return {
      busy: this.isDisabled,
      productData: {
        structureId: null,
        active: true as boolean,
        opaque: null as boolean | null,
        defaultFor: null as string | null,
      } as Product,
      userStructure: null as number | null,
      structures: [] as Record<string, any>[],
      vatRates: [] as Rate[],
      formModel: {},
      formRules: {},
      viewportWidth: 0,
      displayTooltip: false,
      tooltipMessage: '',
      addons: 0,
      selectedStructureDetails: null as Record<string, any> | null,
      structureConfigRequestId: 0,
      accountingAccountAutoSync: true,
      accountingAccountManuallyEdited: false,
      lastAutoFilledAccountingAccount: null as string | null,
      hydratingProductData: false,
    }
  },
  computed: {
    ...mapState(['user']),
    formatFee(): string {
      if (
        this.productData.unitPriceExcludingTaxes &&
        (this.productData.type == 'percent_seller' || this.productData.type == 'percent_buyer')
      ) {
        return (parseFloat(this.productData.unitPriceExcludingTaxes) * 100).toFixed(2) + ' % '
      } else {
        return ''
      }
    },
    isValid() {
      if (
        this.productData.unitPriceExcludingTaxes &&
        !(
          Math.abs(parseFloat(this.productData.unitPriceExcludingTaxes)) >= 0 &&
          Math.abs(parseFloat(this.productData.unitPriceExcludingTaxes)) <= 1
        ) &&
        (this.productData.type == 'percent_seller' || this.productData.type == 'percent_buyer')
      ) {
        return false
      } else {
        return true
      }
    },
    selectedStructure(): Record<string, any> | null {
      if (!this.productData.structureId) {
        return null
      }

      if (
        this.selectedStructureDetails &&
        Number(this.selectedStructureDetails.id) === Number(this.productData.structureId)
      ) {
        return this.selectedStructureDetails
      }

      const structure = this.structures.find(
        (item: Record<string, any>) => Number(item.id) === Number(this.productData.structureId)
      )

      return structure ?? null
    },
    selectedStructureConfiguredCategories(): Record<string, unknown>[] {
      const rawConfigured = this.selectedStructure?.productCategoryCompta
      let parsedCategories: Record<string, unknown>[] = []

      if (Array.isArray(rawConfigured)) {
        parsedCategories = rawConfigured as Record<string, unknown>[]
      } else if (typeof rawConfigured === 'string' && rawConfigured.trim()) {
        try {
          const parsed = JSON.parse(rawConfigured)
          parsedCategories = Array.isArray(parsed) ? (parsed as Record<string, unknown>[]) : []
        } catch (e) {
          parsedCategories = []
        }
      }

      return parsedCategories
        .map((item: Record<string, unknown>) => {
          const normalizedValue = this.normalizeCategoryValue(item?.value)
          return {
            ...item,
            value: normalizedValue,
            label: (item?.label as string) || normalizedValue || '',
          }
        })
        .filter((item: Record<string, unknown>) => typeof item.value === 'string' && item.value)
    },
    productCategories(): { label: string; value: string | null }[] {
      const fallback = [
        { label: 'Honoraires acheteur', value: 'hono_buyer' },
        { label: 'Honoraires vendeur', value: 'hono_seller' },
        { label: 'Honoraires inventaire', value: 'hono_inventory' },
        { label: 'Frais IE', value: 'ie' },
        { label: 'Frais Drouot', value: 'drouot' },
        { label: 'Expert', value: 'expert' },
        { label: 'Transport', value: 'logistic' },
        { label: 'Taxes', value: 'taxes' },
      ]

      const configured = this.selectedStructureConfiguredCategories
      if (Array.isArray(configured) && configured.length) {
        const normalized = configured
          .filter((item: Record<string, unknown>) => item && typeof item.value === 'string' && item.value)
          .map((item: Record<string, unknown>) => ({
            label: (item.label as string) || (item.value as string),
            value: item.value as string,
          }))

        if (normalized.length) {
          return [...normalized, { label: 'Divers', value: null }]
        }
      }

      return [...fallback, { label: 'Divers', value: null }]
    },
    selectableProductCategories(): { label: string; value: string | null }[] {
      const blockedCategories = ['ie', 'drouot']
      const visibleCategories = this.productCategories.filter(
        (category) => !(typeof category.value === 'string' && blockedCategories.includes(category.value))
      )

      if (typeof this.productData.category === 'string' && blockedCategories.includes(this.productData.category)) {
        const currentCategory = this.productCategories.find(
          (category) => category.value === this.productData.category
        )

        if (currentCategory) {
          return [currentCategory, ...visibleCategories]
        }

        return [{ label: this.productData.category, value: this.productData.category }, ...visibleCategories]
      }

      return visibleCategories
    },
    selectedCategoryConfig(): Record<string, unknown> | null {
      const configured = this.selectedStructureConfiguredCategories
      const currentCategory = this.normalizeCategoryValue(this.productData.category)
      if (!Array.isArray(configured) || !currentCategory) {
        return null
      }

      const category = configured.find(
        (item: Record<string, unknown>) => this.normalizeCategoryValue(item?.value) === currentCategory
      )

      return (category as Record<string, unknown>) ?? null
    },
    categoryComptaInfoMessage(): string {
      if (!this.selectedCategoryConfig) {
        return ''
      }

      const categoryLabel =
        (this.selectedCategoryConfig.label as string) ||
        this.productCategories.find((category) => category.value === this.productData.category)?.label ||
        this.productData.category ||
        'cette catégorie'
      const categoryCompta = this.selectedCategoryConfig.compta

      if (categoryCompta === null || categoryCompta === undefined || categoryCompta === '') {
        return ''
      }

      return `Le compte comptable pre-configuré pour cette catégorie de frais (${categoryLabel}) est le : ${categoryCompta}. 4 numéros peuvent être ajoutés pour préciser le frais`
    },
    hasUnsupportedDefaultForValue(): boolean {
      const allowedValues = ['acheteur', 'vendeur', 'ie', 'drouot']
      const currentValue = this.productData.defaultFor

      if (currentValue === null || currentValue === undefined || currentValue === '') {
        return false
      }

      return !allowedValues.includes(String(currentValue))
    },
    isDefaultForFieldDisabled(): boolean {
      return this.hasUnsupportedDefaultForValue
    },
  },
  watch: {
    showFormModal() {
      // This is used as a trick to reset the form data every time the form modal is shown,
      // as well as for the cancel button, so that it doesn't automatically redirect the user to the product list
      this.resetFormData()
    },
    user(newVal) {
      this.addons = newVal.client?.addons ?? 0
      this.structures = newVal.client.structures
      if (this.productData.structureId === null) {
        this.productData.structureId = newVal.client.structures[0]?.id ?? null
        this.vatRates = newVal.client.structures[0]?.taxRate?.rates ?? []
      }
    },
    fetchProductId(newVal) {
      if (Number.isInteger(parseInt(newVal))) {
        this.getProduct(newVal)
      } else {
        this.resetFormData()
      }
    },
    structure(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.productData.structureId = Number(newVal)
      }
    },
    'productData.structureId'(newVal) {
      if (newVal === null || newVal === undefined || newVal === '') {
        this.selectedStructureDetails = null
        this.vatRates = []
        this.productData.category = null
        this.productData.accountingAccount = null
        return
      }

      this.selectedStructureDetails = null

      const structure = this.structures.find((item: Record<string, any>) => Number(item.id) === Number(newVal))
      this.vatRates = structure?.taxRate?.rates ?? []

      if (this.hydratingProductData) {
        return
      }

      this.fetchStructureConfiguration(Number(newVal))

      const categoryStillAvailable = this.productCategories.some(
        (category) => this.normalizeCategoryValue(category.value) === this.normalizeCategoryValue(this.productData.category)
      )
      if (!categoryStillAvailable) {
        this.productData.category = null
      }

      if (this.accountingAccountAutoSync && !this.accountingAccountManuallyEdited) {
        this.productData.accountingAccount = null
        this.lastAutoFilledAccountingAccount = null
      }

      this.applyCategoryComptaIfEmpty()
    },
    'productData.category'() {
      this.applyCategoryComptaIfEmpty()
    },
  },
  mounted() {
    if (this.user) {
      this.structures = this.user.client.structures
      this.vatRates = this.user.client.structures[0]?.taxRate?.rates ?? []
    }

    if (Number.isInteger(parseInt(this.fetchProductId))) {
      this.getProduct(this.fetchProductId)
    } else {
      this.resetFormData()
    }

    this.viewportWidth = window.innerWidth

    window.addEventListener('resize', this.onResize)

    this.addons = this.user.client?.addons ?? 0
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize() {
      this.viewportWidth = window.innerWidth
    },
    resetFormData() {
      const preferredStructureId = this.structure ?? null
      const preferredStructure = this.structures.find(
        (item: Record<string, any>) => Number(item.id) === Number(preferredStructureId)
      )
      const defaultStructureId = preferredStructure?.id ?? this.structures[0]?.id ?? null
      const defaultStructure = this.structures.find(
        (item: Record<string, any>) => Number(item.id) === Number(defaultStructureId)
      )

      this.productData = {
        structureId: defaultStructureId,
        active: true,
        opaque: null,
        category: null,
        accountingAccount: null,
      }
      this.accountingAccountAutoSync = true
      this.accountingAccountManuallyEdited = false
      this.lastAutoFilledAccountingAccount = null

      this.vatRates = defaultStructure?.taxRate?.rates ?? []

      if (defaultStructureId !== null && defaultStructureId !== undefined) {
        this.fetchStructureConfiguration(Number(defaultStructureId))
      }
    },
    fetchStructureConfiguration(structureId: number) {
      if (!Number.isFinite(structureId)) {
        return
      }

      const requestId = ++this.structureConfigRequestId

      this.$api
        .get(`/structure/${structureId}`)
        .then((response) => {
          if (requestId !== this.structureConfigRequestId) {
            return
          }

          const apiStructure = response?.data?.data
          if (!apiStructure) {
            return
          }

          this.selectedStructureDetails = apiStructure
          this.vatRates = apiStructure?.taxRate?.rates ?? this.vatRates

          const categoryStillAvailable = this.productCategories.some(
            (category) => category.value === this.productData.category
          )
          if (!categoryStillAvailable) {
            this.productData.category = null
          }

          this.applyCategoryComptaIfEmpty()
        })
        .catch(() => {
          if (requestId !== this.structureConfigRequestId) {
            return
          }

          this.selectedStructureDetails = null
        })
    },
    normalizeCategoryValue(value: unknown): string | null {
      if (value === null || value === undefined) {
        return null
      }

      const normalized = String(value).trim()
      return normalized ? normalized : null
    },
    isAccountingAccountEmpty(): boolean {
      const value = this.productData.accountingAccount
      if (value === null || value === undefined) {
        return true
      }

      return String(value).trim() === ''
    },
    onAccountingAccountInput(value: string) {
      if (this.hydratingProductData) {
        return
      }

      const normalizedValue = value === null || value === undefined ? '' : String(value).trim()
      const normalizedAutoFilled = this.lastAutoFilledAccountingAccount ? this.lastAutoFilledAccountingAccount.trim() : ''

      if (normalizedValue === '') {
        this.accountingAccountAutoSync = true
        this.accountingAccountManuallyEdited = false
        this.lastAutoFilledAccountingAccount = null
        return
      }

      if (normalizedValue !== normalizedAutoFilled) {
        this.accountingAccountAutoSync = false
        this.accountingAccountManuallyEdited = true
      }
    },
    applyCategoryComptaIfEmpty() {
      const compta = this.selectedCategoryConfig?.compta

      if (
        this.accountingAccountAutoSync &&
        !this.accountingAccountManuallyEdited &&
        compta !== null &&
        compta !== undefined &&
        compta !== ''
      ) {
        const normalizedCompta = String(compta)
        this.productData.accountingAccount = normalizedCompta
        this.lastAutoFilledAccountingAccount = normalizedCompta
      }
    },
    formatFloat(value = ''): string {
      return value.replace(',', '.')
    },
    cancelForm() {
      if (this.showFormModal) {
        this.$emit('closeModal')
      } else {
        this.$router.push({ name: 'products' })
      }
    },
    getProduct(productId: string) {
      this.busy = true
      this.$api
        .get(`/product/${productId}`)
        .then((response) => {
          const apiResponse = response.data as ApiResponse

          this.hydratingProductData = true
          this.productData = apiResponse.data
          this.accountingAccountAutoSync = this.isAccountingAccountEmpty()
          this.accountingAccountManuallyEdited = false
          this.lastAutoFilledAccountingAccount = null

          if (this.productData.structureId !== null && this.productData.structureId !== undefined) {
            this.fetchStructureConfiguration(Number(this.productData.structureId))
          } else {
            this.applyCategoryComptaIfEmpty()
          }

          this.$nextTick(() => {
            this.hydratingProductData = false
          })
        })
        .catch(() => {
          this.$notify({
            type: 'error',
            title: 'Erreur',
            message: 'Une erreur est survenue. Veuillez nous excuser pour la gêne occasionnée.',
          })
        })
        .finally(() => {
          this.hydratingProductData = false
          this.busy = false
        })
    },
    reset() {
      ;(this.$refs.myForm as ElForm).resetFields()
    },
    submit() {
      this.submitAsync()
    },
    async submitAsync() {
      this.tooltipMessage = ''
      this.displayTooltip = false

      // Checking if at least the product name (or company name) is entered
      if (this.productData.name == '' || this.productData.name == null) {
        this.tooltipMessage = 'Merci de préciser au moins un nom de produit.'
        this.displayTooltip = true

        return false
      }

      // checking %
      if (
        this.productData.unitPriceExcludingTaxes &&
        !(
          Math.abs(parseFloat(this.productData.unitPriceExcludingTaxes)) >= 0 &&
          Math.abs(parseFloat(this.productData.unitPriceExcludingTaxes)) <= 1
        ) &&
        (this.productData.type == 'percent_seller' || this.productData.type == 'percent_buyer')
      ) {
        this.tooltipMessage = 'Le Prix Unitaire en poucentage doit être compris entre 0 et 1.'
        this.displayTooltip = true
        return false
      }

      // Checking TVA rate
      if (this.productData.tvaRate == null || this.productData.tvaRate === '') {
        this.tooltipMessage = 'Merci de saisir un taux de TVA.'
        this.displayTooltip = true
        return false
      }

      // Checking opaque
      if (this.productData.opaque == null) {
        this.tooltipMessage = "Régime au regard de la TVA : Merci de choisir de choisir régime opaque ou transparent."
        this.displayTooltip = true
        return false
      }

      // Checking opaque
      if (this.productData.accountingAccount == null) {
        this.tooltipMessage =
          "Vous devez choisir un compte comptable avant application d'un frais sur un lot ou une facture"
        this.displayTooltip = true
        return false
      }

      // Checking structure
      if (this.productData.structureId == null) {
        this.tooltipMessage = 'Merci de choisir une structure.'
        this.displayTooltip = true

        return false
      }

      if (!(await this.validateDefaultForUniqueness())) {
        return false
      }

      this.busy = true
      ;(this.$refs.myForm as ElForm).validate((valid: boolean) => {
        if (valid) {
          // Convert int value to string
          Object.keys(this.productData).map((key) => {
            if (key === 'unitPriceExcludingTaxes' || key === 'tvaRate') {
              this.productData[key] = this.formatFloat(this.productData[key]?.toString())
            }
          })

          delete this.productData.structure

          const data = {
            ...this.productData,
          }

          if (!this.productData.id) {
            // Store
            this.$api
              .post('/product', data)
              .then((response) => {
                const apiResponse = response.data as ApiResponse

                this.productData = apiResponse.data
                this.$notify({
                  type: 'success',
                  title: 'Succès',
                  message: 'Produit créé avec succès !',
                })
                this.$emit('productCreated', apiResponse.data)
              })
              .catch((error) => {
                if (error.response) {
                  const apiResponse = error.response.data as ApiResponse

                  this.$notify({
                    type: 'error',
                    title: 'Erreur',
                    message:
                      apiResponse.message ??
                      'Une erreur est survenue. Veuillez nous excuser pour la gêne occasionnée.',
                  })
                }
              })
              .finally(() => {
                this.busy = false
              })
          } else {
            // Edit
            const loading = this.$loading({
              target: '.form-panel',
              text: 'Chargement des données...',
            })
            this.$api
              .put(`/product/${this.productData.id}`, data)
              .then((response) => {
                const apiResponse = response.data as ApiResponse

                this.productData = apiResponse.data
                this.$notify({
                  type: 'success',
                  title: 'Succès',
                  message: 'Produit modifié avec succès !',
                })
                this.$emit('productUpdated', apiResponse.data)
              })
              .catch((error) => {
                if (error.response) {
                  const apiResponse = error.response.data as ApiResponse

                  this.$notify({
                    type: 'error',
                    title: 'Erreur',
                    message:
                      apiResponse.message ??
                      'Une erreur est survenue. Veuillez nous excuser pour la gêne occasionnée.',
                  })
                }
              })
              .finally(() => {
                this.busy = false
                loading.close()
              })
          }
        }
        this.busy = false
      })
    },
    async validateDefaultForUniqueness(): Promise<boolean> {
      // Legacy values are read-only in the UI and must not block saving other fields.
      if (this.hasUnsupportedDefaultForValue) {
        return true
      }

      if (!this.productData.defaultFor || !this.productData.structureId) {
        return true
      }

      try {
        const response = await this.$api.get('/products', {
          params: {
            structure: this.productData.structureId,
            page: 'productForm',
          },
        })

        const apiResponse = response.data as ApiResponse
        const products = Array.isArray(apiResponse.data) ? (apiResponse.data as Product[]) : []
        const duplicateProduct = products.find((product: Product) => {
          const sameDefaultFor = product.defaultFor === this.productData.defaultFor
          const sameProduct = Number(product.id) === Number(this.productData.id)

          return sameDefaultFor && !sameProduct
        })

        if (duplicateProduct) {
          this.tooltipMessage =
            'Ce frais par défaut existe déjà pour cette structure. Un seul frais par défaut est autorisé par type.'
          this.displayTooltip = true
          return false
        }

        return true
      } catch (error) {
        this.tooltipMessage =
          'Impossible de vérifier l\'unicité du frais par défaut pour cette structure. Veuillez réessayer.'
        this.displayTooltip = true
        return false
      }
    },
    disabledProductField() {
      if (this.productData.category == 'ie' || this.productData.category == 'drouot') {
        return true
      } else {
        return false
      }
    },
  },
})
